/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { ProductList } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import {
    getOrderTemplates,
    GetOrderTemplatesInput,
    getPaginatedOrderTemplateLinesAction,
    GetPaginatedOrderTemplateLinesInput,
    QueryResultSettingsProxy,
    OrderTemplate,
    OrderTemplateLine,
    createInventoryAvailabilitySearchCriteria,
    mapAggregatedProductInventoryInformation,
    IProductInventoryInformation
} from '@msdyn365-commerce-modules/retail-actions';
import { getEstimatedAvailabilityAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

export interface OrderTemplateInventoryData {
    LineId?: string;
    isStockAvailable: boolean;
}

export class OrderTemplateWithInventory extends OrderTemplate {
    public inventory: IProductInventoryInformation[];

    constructor(
        productList: ProductList,
        orderTemplateLines: OrderTemplateLine[],
        totalLines: number,
        inventory: IProductInventoryInformation[]
    ) {
        super(productList, orderTemplateLines, totalLines);
        this.inventory = inventory;
    }
}

/**
 *  Input class for the GetOrderTemplates data action.
 */
export class GetFullOrderTemplateWithInventoryInput implements IActionInput {
    public orderTemplateId: string;

    public queryResultSettings: IQueryResultSettings;

    constructor(orderTemplateId: string, queryResultSettings: IQueryResultSettings) {
        this.orderTemplateId = orderTemplateId;
        this.queryResultSettings = queryResultSettings;
    }

    public getCacheKey = () => `GetFullOrderTemplateWithInventory|${this.orderTemplateId}`;

    public getCacheObjectType = () => 'GetFullOrderTemplateWithInventory';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetOrderTemplate method.
 * @param inputData The input data passed to the createInput method.
 * @param orderTemplateId
 */
export const createGetFullOrderTemplateWithInventoryInput = (
    inputData: ICreateActionContext,
    orderTemplateId: string
): GetFullOrderTemplateWithInventoryInput => {
    const { requestContext } = inputData;
    if (!requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full order templates. User is not authenticated.');
    }
    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;
    queryResultSettings.count = true;

    return new GetFullOrderTemplateWithInventoryInput(orderTemplateId, queryResultSettings);
};

/**
 * The action method for the GetFullOrderTemplateWithInventory data action.
 * @param input The action input.
 * @param ctx The action context.
 */
export async function GetFullOrderTemplateWithInventoryAction(
    input: GetFullOrderTemplateWithInventoryInput,
    ctx: IActionContext
): Promise<OrderTemplateWithInventory> {
    if (!ctx.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full order template. User is not authenticated.');
    }

    const inputParams = new GetOrderTemplatesInput(input.orderTemplateId);
    const productLists: ProductList[] = await getOrderTemplates(inputParams, ctx);

    if (!productLists || productLists.length !== 1) {
        throw new Error('Unable to get full order template. Order template was not found.');
    }

    const inputLines = new GetPaginatedOrderTemplateLinesInput(productLists[0].Id, input.queryResultSettings);
    const linesState = await getPaginatedOrderTemplateLinesAction(inputLines, ctx);

    let productAvailabilities: IProductInventoryInformation[] = [];
    const productIds = linesState.lines.map(line => line.productListLine.ProductId!);
    const searchCriteria = createInventoryAvailabilitySearchCriteria(ctx, productIds, true);
    const linesProductAvailabilities = await getEstimatedAvailabilityAsync({ callerContext: ctx, bypassCache: 'get' }, searchCriteria);
    if (
        linesProductAvailabilities &&
        linesProductAvailabilities.ProductWarehouseInventoryAvailabilities &&
        linesProductAvailabilities.ProductWarehouseInventoryAvailabilities.length > 0
    ) {
        productAvailabilities = mapAggregatedProductInventoryInformation(ctx, linesProductAvailabilities);
    }

    return new OrderTemplateWithInventory(productLists[0], linesState.lines, linesState.totalCount, productAvailabilities);
}

/**
 * The GetFullOrderTemplateWithInventory Data Action
 * Returns order templates.
 */
export const GetFullOrderTemplateWithInventoryActionDataAction = createObservableDataAction<OrderTemplateWithInventory>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/get-full-order-template',
    action: <IAction<OrderTemplateWithInventory>>GetFullOrderTemplateWithInventoryAction,
    input: createGetFullOrderTemplateWithInventoryInput
});

export default GetFullOrderTemplateWithInventoryActionDataAction;
